/**
 * The one date/number formatter in the product (P-041).
 *
 * monday renders **en-US** everywhere — `Created by … on Jun 6, 2022`
 * (`[c:prod-board-info-permissions]`, `[ui:library/board-02-description.png]`) — regardless of
 * the browser's locale: the account has one language and the whole UI vocabulary is English.
 * Ours leaked the browser locale wherever a `toLocale*` call omitted its first argument, which
 * on an Italian machine turned that row into `8 lug 2025` (the bug the user reported).
 *
 * Every date, time and grouped number the product prints goes through this file. The lint-level
 * rule is simple: **no `toLocaleDateString` / `toLocaleTimeString` / `toLocaleString` /
 * `new Intl.*` in product code** — call one of these instead. The only legitimate direct `Intl`
 * uses left are time-zone detection (`resolvedOptions().timeZone`) and the column-type
 * formatters in `@elizmonday/shared`, which already pin `'en-US'` / `'en-CA'`.
 */
/** monday's locale. Not the browser's, and not configurable — see the file header. */
export const MONDAY_LOCALE = 'en-US';
function toDate(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
const CACHE = new Map();
function formatter(options) {
    const key = JSON.stringify(options);
    let cached = CACHE.get(key);
    if (!cached) {
        cached = new Intl.DateTimeFormat(MONDAY_LOCALE, options);
        CACHE.set(key, cached);
    }
    return cached;
}
/**
 * monday's long date: **`Jun 6, 2022`**. Used by the board-info popover, the archive/trash
 * tables, the docs list, the admin directory and every "created on" line.
 */
export function formatMondayDate(value, options) {
    const date = toDate(value);
    if (date === null)
        return '';
    return formatter({ month: 'short', day: 'numeric', year: 'numeric', ...options }).format(date);
}
/** monday's short date, no year: **`Jun 6`** (update composer, My work). */
export function formatMondayDayMonth(value) {
    const date = toDate(value);
    if (date === null)
        return '';
    return formatter({ month: 'short', day: 'numeric' }).format(date);
}
/** monday's clock: **`4:05 PM`**. */
export function formatMondayTime(value) {
    const date = toDate(value);
    if (date === null)
        return '';
    return formatter({ hour: 'numeric', minute: '2-digit' }).format(date);
}
/** `Jun 6, 2022, 4:05 PM` — the activity/audit stamp. */
export function formatMondayDateTime(value, options) {
    const date = toDate(value);
    if (date === null)
        return '';
    return formatter({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...options,
    }).format(date);
}
const NUMBER_CACHE = new Map();
/** Grouped numbers in monday's locale (`1,234`), never the browser's (`1.234`). */
export function formatMondayNumber(value, options) {
    if (!Number.isFinite(value))
        return '';
    const key = JSON.stringify(options ?? {});
    let cached = NUMBER_CACHE.get(key);
    if (!cached) {
        cached = new Intl.NumberFormat(MONDAY_LOCALE, options);
        NUMBER_CACHE.set(key, cached);
    }
    return cached.format(value);
}
//# sourceMappingURL=dates.js.map